import { Button, Stack } from '@optimus-web/core';
import { FormInputField, lt, useFormControls } from '@mews/framework';
import { EnumSelectField, SelectField } from '@mews-commander/form';
import { RoutingRuleTriggerType } from '@mews-commander/data-access';
import { FilterSidebar, FilterSidebarProps } from '@mews/b2b-ui';
import {
    RoutingRulesListFilterSchemaKeys,
    RoutingRulesListFilterValues,
} from '../../schemas/routingRulesListFilterSchema';
import { useRoutingRuleListFilterDefaultValues } from '../../utils/useRoutingRuleListFilterDefaultValues';
import { CompanySearchField } from '../../../../components/form/hookFields/asyncSelectField/CompanySearchField/CompanySearchField';
import { TravelAgencySearchField } from '../../../../components/form/hookFields/asyncSelectField/TravelAgencySearchField/TravelAgencySearchField';
import { ReservationGroupSearchField } from '../../../../components/form/hookFields/asyncSelectField/ReservationGroupSearchField/ReservationGroupSearchField';
import { ReservationSearchField } from '../../../../components/form/hookFields/asyncSelectField/ReservationSearchField/ReservationSearchField';
import { CustomerProfileSearchField } from '../../../../components/form/hookFields/asyncSelectField/CustomerProfileSearchField/CustomerProfileSearchField';
import { useGroupedByServiceRatesOptions } from './useGroupedByServiceRatesOptions';

interface RoutingRulesTableFilterFormProps extends FilterSidebarProps {
    displayLegacy: boolean;
    onSubmit: (values: RoutingRulesListFilterValues) => void;
}

export const RoutingRulesTableFilterForm: React.FC<RoutingRulesTableFilterFormProps> = ({
    displayLegacy,
    onSubmit,
    ...others
}) => {
    const defaultValues = useRoutingRuleListFilterDefaultValues(displayLegacy);

    const { renderFormContent, submit, reset } = useFormControls<RoutingRulesListFilterValues>({
        defaultValues,
        onSubmit,
    });

    const { data: rateOptions = [], isLoading } = useGroupedByServiceRatesOptions();

    return renderFormContent(
        <FilterSidebar
            {...others}
            actionWrapperProps={{ distribution: 'space-between' }}
            primaryButton={{ label: lt('ApplyFilters'), type: 'submit', onClick: submit }}
            actions={
                <Button type="reset" onClick={() => reset(defaultValues)}>
                    {lt('Reset')}
                </Button>
            }
        >
            <Stack vertical spacing="300">
                <FormInputField
                    name={RoutingRulesListFilterSchemaKeys.Name}
                    label={lt('BillingAutomationPageTableFiltersNameFieldLabel')}
                    placeholder={lt('BillingAutomationPageTableFiltersNamePlaceholder')}
                />

                <EnumSelectField
                    multiselect
                    required
                    omitHint
                    name={RoutingRulesListFilterSchemaKeys.Timing}
                    label={lt('BillingAutomationPageTableFiltersTimingFieldLabel')}
                    placeholder={lt('BillingAutomationPageTableFiltersTimingPlaceholder')}
                    enumClass={RoutingRuleTriggerType}
                    enumPrefix="RoutingRuleTriggerType"
                    enabledValues={
                        displayLegacy
                            ? undefined
                            : [RoutingRuleTriggerType.Continuous, RoutingRuleTriggerType.Recurring]
                    }
                />

                <CompanySearchField
                    multiselect
                    name={RoutingRulesListFilterSchemaKeys.Companies}
                    label={lt('BillingAutomationPageTableFiltersCompaniesFieldLabel')}
                    placeholder={lt('BillingAutomationPageTableFiltersCompaniesPlaceholder')}
                />

                <TravelAgencySearchField
                    multiselect
                    name={RoutingRulesListFilterSchemaKeys.TravelAgencies}
                    label={lt('BillingAutomationPageTableFiltersTravelAgenciesFieldLabel')}
                    placeholder={lt('BillingAutomationPageTableFiltersTravelAgenciesPlaceholder')}
                />

                <SelectField
                    multiselect
                    name={RoutingRulesListFilterSchemaKeys.Rates}
                    loader={isLoading}
                    options={rateOptions}
                    label={lt('BillingAutomationPageTableFiltersRatesFieldLabel')}
                    placeholder={lt('BillingAutomationPageTableFiltersRatesPlaceholder')}
                />

                <CustomerProfileSearchField
                    multiselect
                    name={RoutingRulesListFilterSchemaKeys.GuestProfiles}
                    label={lt('BillingAutomationPageTableFiltersGuestProfilesFieldLabel')}
                    placeholder={lt('BillingAutomationPageTableFiltersGuestProfilesPlaceholder')}
                />

                <ReservationGroupSearchField
                    multiselect
                    name={RoutingRulesListFilterSchemaKeys.Groups}
                    label={lt('BillingAutomationPageTableFiltersGroupsFieldLabel')}
                    placeholder={lt('BillingAutomationPageTableFiltersGroupsPlaceholder')}
                />

                <ReservationSearchField
                    multiselect
                    name={RoutingRulesListFilterSchemaKeys.Reservations}
                    label={lt('BillingAutomationPageTableFiltersReservationsFieldLabel')}
                    placeholder={lt('BillingAutomationPageTableFiltersReservationsPlaceholder')}
                />
            </Stack>
        </FilterSidebar>
    );
};
